/** Checkpoint verification methods exposed in Setup (matches DB enum + mobile capture). */

export const CHECKPOINT_METHOD_OPTIONS = [
  {
    value: "qr",
    label: "QR tag",
    hint: "Guard scans a posted QR code. Strongest when the tag is mounted at the stop.",
  },
  {
    value: "nfc",
    label: "NFC tag",
    hint: "Guard taps a mounted NFC tag. Best for indoor posts with reliable tap range.",
  },
  {
    value: "gps_geofence",
    label: "GPS geofence",
    hint: "Drive-by or vehicle patrol: the app proves the guard was near the coordinates. Set a geofence radius below.",
  },
  {
    value: "manual",
    label: "Manual check-in",
    hint: "Guard confirms presence with a note or photo when tags are impractical. Lower assurance; use sparingly.",
  },
] as const;

export type CheckpointMethodValue = (typeof CHECKPOINT_METHOD_OPTIONS)[number]["value"];

export const DEFAULT_GPS_GEOFENCE_RADIUS_METERS = 75;

export function formatCheckpointMethod(value: string): string {
  const match = CHECKPOINT_METHOD_OPTIONS.find((option) => option.value === value);
  return match?.label ?? value.replace(/_/g, " ");
}

/** QR and NFC need desk-minted material. GPS geofence and manual never use token kits. */
export function checkpointUsesPhysicalTags(value: string): boolean {
  const method = value.trim().toLowerCase();
  return method === "qr" || method === "nfc";
}

/**
 * Setup's primary mint action must not promise QR-only output for an NFC-preferred or
 * unknown checkpoint. The kit still contains the shared QR/NFC payload either way.
 */
export function checkpointMintPrimaryLabel(value: string | null | undefined): "Mint QR" | "Mint kit" {
  return value?.trim().toLowerCase() === "qr" ? "Mint QR" : "Mint kit";
}

export function checkpointMethodHint(value: string): string | null {
  const match = CHECKPOINT_METHOD_OPTIONS.find((option) => option.value === value);
  return match?.hint ?? null;
}
